/**
 * Astro Blog Enhancer — DOM transformer that upgrades plain HTML/Markdown
 * into premium themed astrology components. Runs AFTER `shlokaEnhancer` in the
 * `renderBlogContent` pipeline so BOTH the admin preview AND the published blog
 * page render identically.
 *
 * What it detects and wraps:
 *
 *   1. LABEL-BASED SHLOKAS ("Shloka:" / "Source:" / "Meaning:" / "Interpretation:"
 *      plus Hindi equivalents) — grouped into a single `blockquote.shloka` card
 *      with structured Source / Sanskrit / Meaning / Interpretation zones.
 *
 *   2. KEY TAKEAWAYS — heading matching `मुख्य निष्कर्ष|Key Takeaways|मुख्य बिंदु`
 *      followed by a bullet list → wrapped in `.astro-callout.takeaway`.
 *
 *   3. MEDINI JYOTISH INSIGHT — heading matching `मेदनी|जगत लग्न|Mundane` →
 *      following prose wrapped in `.astro-callout.medini`.
 *
 *   4. COUNTRY IMPACT SECTIONS — headings matching `भारत पर प्रभाव|India Impact|
 *      UAE Impact|दुबई|विश्व पर प्रभाव|World Impact` → `.astro-callout.country-XX`.
 *
 *   5. CAUTION / IMPORTANT PREDICTION / ZODIAC SUMMARY — headings matching
 *      `सावधानी|चेतावनी|Caution|Warning`, `महत्वपूर्ण भविष्यवाणी|Important Prediction`,
 *      `12 राशियों|All Zodiacs|राशि सारांश` → respective callouts.
 *
 * Design principles:
 *   • Zero LLM cost — pure DOM transform, safe to run on every render.
 *   • Idempotent — already-wrapped `.astro-callout` blocks are skipped.
 *   • Non-destructive — if a heading has no matching content, nothing is wrapped.
 *   • Conservative match — headings must fully-match the regex (title-line), not
 *     accidental mentions inside body text.
 */

// ---------- Section detectors ----------
// Each detector returns { kind, icon, label, cssClass } if the header matches.
const SECTION_DETECTORS = [
  {
    kind: 'takeaway',
    icon: '💎',
    label: 'मुख्य निष्कर्ष',
    cssClass: 'astro-callout takeaway',
    // "मुख्य निष्कर्ष", "Key Takeaways", "मुख्य बिंदु", "टेकअवे"
    match: /^\s*(?:💎\s*)?(मुख्य\s*(?:निष्कर्ष|बिंदु|बातें)|key\s*takeaways?|takeaways?)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'medini',
    icon: '🌍',
    label: 'मेदनी ज्योतिषीय निष्कर्ष',
    cssClass: 'astro-callout medini',
    match: /^\s*(?:🌍\s*)?(मेदनी\s*ज्योतिष(?:ीय)?(?:\s*विश्लेषण|\s*निष्कर्ष)?|mundane\s*astrology|जगत\s*लग्न)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'country-india',
    icon: '🇮🇳',
    label: 'भारत पर प्रभाव',
    cssClass: 'astro-callout country country-india',
    match: /^\s*(?:🇮🇳\s*)?(भारत\s*(?:पर\s*(?:संभावित\s*)?प्रभाव|प्रभाव)|india(?:\s*impact)?)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'country-uae',
    icon: '🇦🇪',
    label: 'UAE पर प्रभाव',
    cssClass: 'astro-callout country country-uae',
    match: /^\s*(?:🇦🇪\s*)?(uae\s*(?:\(?\s*दुबई\s*\)?)?\s*(?:पर\s*(?:संभावित\s*)?प्रभाव|impact)?|दुबई\s*(?:पर\s*प्रभाव)?)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'country-world',
    icon: '🌐',
    label: 'विश्व पर प्रभाव',
    cssClass: 'astro-callout country country-world',
    match: /^\s*(?:🌐\s*)?(विश्व\s*(?:पर\s*(?:संभावित\s*)?प्रभाव|प्रभाव)|world(?:\s*impact)?|global\s*impact)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'caution',
    icon: '⚠️',
    label: 'सावधानी',
    cssClass: 'astro-callout caution',
    match: /^\s*(?:⚠️\s*)?(सावधानी|चेतावनी|caution|warning)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'prediction',
    icon: '🔮',
    label: 'महत्वपूर्ण भविष्यवाणी',
    cssClass: 'astro-callout prediction',
    match: /^\s*(?:🔮\s*)?(महत्वपूर्ण\s*(?:भविष्यवाणी|प्रेडिक्शन)|important\s*prediction|key\s*prediction)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'zodiac',
    icon: '♈',
    label: '12 राशियों पर प्रभाव',
    cssClass: 'astro-callout zodiac-summary',
    match: /^\s*(?:♈\s*)?(12\s*राशि(?:यों|ों)?\s*(?:पर\s*(?:संभावित\s*)?प्रभाव|का\s*फल)?|all\s*(?:12\s*)?zodiacs?|राशि\s*सारांश|rashi\s*summary)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'direct-answer',
    icon: '⚡',
    label: 'सीधा उत्तर',
    cssClass: 'astro-callout direct-answer',
    match: /^\s*(?:⚡\s*)?(सीधा\s*उत्तर|तुरंत\s*उत्तर|direct\s*answer|quick\s*answer)\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
  {
    kind: 'shastra-ref',
    icon: '📜',
    label: 'शास्त्रीय संदर्भ',
    cssClass: 'astro-callout shastra-ref',
    match: /^\s*(?:📜\s*)?(शास्त्रीय\s*संदर्भ|शास्त्र\s*प्रमाण|scripture\s*(?:reference|proof)|shastra\s*(?:reference|pramana))\s*:?\s*$/i,
    absorbUntilNextHeading: true,
  },
];

// Detect a heading tag by text content
function findSectionMatch(el) {
  const tag = (el.tagName || '').toLowerCase();
  if (!/^h[1-6]$/.test(tag)) return null;
  const text = (el.textContent || '').trim();
  if (!text || text.length > 120) return null;
  for (const det of SECTION_DETECTORS) {
    if (det.match.test(text)) return det;
  }
  return null;
}

// Return the numeric heading level (1-6) or null.
function headingLevel(el) {
  const m = /^h([1-6])$/i.exec(el?.tagName || '');
  return m ? parseInt(m[1], 10) : null;
}

// Determine if we should stop absorbing siblings into a callout.
// Stop at another heading (any level), OR at another callout, OR at a shloka block.
function isBoundary(el) {
  if (!el) return true;
  const tag = (el.tagName || '').toLowerCase();
  if (/^h[1-6]$/.test(tag)) return true;
  if (el.classList) {
    if (el.classList.contains('astro-callout')) return true;
    if (tag === 'blockquote' && el.classList.contains('shloka')) return true;
  }
  return false;
}

// ---------- Label-based shloka detector ----------
// Detects a structured block like:
//   <p><strong>Shloka:</strong> ...verse...</p>
//   <p><strong>Source:</strong> ...</p>
//   <p><strong>Meaning:</strong> ...</p>
//   <p><strong>Interpretation:</strong> ...</p>
// Also handles Hindi labels (श्लोक, स्रोत, हिन्दी अर्थ, व्याख्या).
const LABEL_PATTERNS = {
  shloka:         /^(shloka|श्लोक|मन्त्र|मंत्र|verse|sanskrit\s*verse)\s*[:.]?\s*/i,
  source:         /^(source|स्रोत|शास्त्र|ग्रंथ|scripture|reference|प्रमाण)\s*[:.]?\s*/i,
  meaning:        /^(meaning|अर्थ|हिन्दी\s*अर्थ|भावार्थ|translation|hindi\s*meaning)\s*[:.]?\s*/i,
  interpretation: /^(interpretation|व्याख्या|शास्त्रीय\s*व्याख्या|commentary|व्यावहारिक\s*अर्थ|explanation)\s*[:.]?\s*/i,
};

function classifyLabelledParagraph(el) {
  if (!el || (el.tagName || '').toLowerCase() !== 'p') return null;
  // Prefer explicit <strong>/<b> label at the start
  const first = el.firstElementChild;
  let labelText = '';
  if (first && /^(strong|b)$/i.test(first.tagName)) {
    labelText = (first.textContent || '').trim();
  } else {
    // Fallback — first ~40 chars of text
    const raw = (el.textContent || '').trim();
    labelText = raw.slice(0, 40);
  }
  for (const [kind, re] of Object.entries(LABEL_PATTERNS)) {
    if (re.test(labelText)) {
      // Return HTML with the leading label stripped from body
      let bodyHtml = el.innerHTML;
      if (first && /^(strong|b)$/i.test(first.tagName)) {
        // Remove the <strong> label element cleanly
        const clone = el.cloneNode(true);
        const strong = clone.firstElementChild;
        if (strong) strong.remove();
        bodyHtml = clone.innerHTML.replace(/^\s*[:—-]\s*/, '').trim();
      } else {
        bodyHtml = bodyHtml.replace(LABEL_PATTERNS[kind], '').trim();
      }
      return { kind, body: bodyHtml };
    }
  }
  return null;
}

// ---------- Main enhancer ----------
export function enhanceAstroBlog(html) {
  if (!html || typeof html !== 'string') return html || '';
  return enhanceAstroCallouts(enhanceLabelledShlokas(html));
}

/**
 * Pass 1 only — wrap label-based shloka groups
 * (`<p><strong>Shloka:</strong>…</p>` + Source/Meaning/Interpretation).
 * Runs BEFORE `enhanceShlokas` so it gets first crack at labelled verses.
 */
export function enhanceLabelledShlokas(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof DOMParser === 'undefined') return html;

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  } catch {
    return html;
  }
  const root = doc.getElementById('__root');
  if (!root) return html;

  // If the entire article is wrapped in <article class="virat-blog"> (auto-blog
  // engine emits this), operate on the article's children directly — otherwise
  // section headings would never be top-level and detection would miss them.
  const articleWrap = root.querySelector('article.virat-blog') || root.querySelector('article');
  const scope = articleWrap || root;

  const allNodes = Array.from(scope.children);
  const consumed = new Set();

  for (let i = 0; i < allNodes.length; i++) {
    const el = allNodes[i];
    if (!el || consumed.has(el)) continue;
    const cls = classifyLabelledParagraph(el);
    if (!cls || cls.kind !== 'shloka') continue;

    const parts = { shloka: cls.body };
    consumed.add(el);
    let j = i + 1;
    while (j < allNodes.length) {
      const nxt = allNodes[j];
      if (!nxt || consumed.has(nxt)) break;
      const sub = classifyLabelledParagraph(nxt);
      if (!sub || sub.kind === 'shloka') break;
      if (parts[sub.kind]) break;
      parts[sub.kind] = sub.body;
      consumed.add(nxt);
      j += 1;
    }

    const card = doc.createElement('blockquote');
    card.className = 'shloka labelled';

    const title = doc.createElement('p');
    title.className = 'shloka-title';
    title.textContent = '📜 शास्त्रीय संदर्भ';
    card.appendChild(title);

    if (parts.source) {
      const src = doc.createElement('p');
      src.className = 'source-line';
      src.innerHTML = parts.source;
      card.appendChild(src);
    }

    const sanskrit = doc.createElement('p');
    sanskrit.className = 'sanskrit';
    sanskrit.innerHTML = parts.shloka
      .replace(/।\s+(?=[\u0900-\u097F])/g, '।<br/>')
      .replace(/॥\s+(?=[\u0900-\u097F])/g, '॥<br/>');
    card.appendChild(sanskrit);

    if (parts.meaning) {
      const label = doc.createElement('p');
      label.className = 'meaning-label';
      label.textContent = 'हिन्दी अर्थ:';
      card.appendChild(label);
      const m = doc.createElement('p');
      m.className = 'meaning';
      m.innerHTML = parts.meaning;
      card.appendChild(m);
    }

    if (parts.interpretation) {
      const label = doc.createElement('p');
      label.className = 'interpretation-label';
      label.textContent = 'व्यावहारिक अर्थ:';
      card.appendChild(label);
      const interp = doc.createElement('p');
      interp.className = 'interpretation';
      interp.innerHTML = parts.interpretation;
      card.appendChild(interp);
    }

    el.parentNode.replaceChild(card, el);
  }
  consumed.forEach((n) => {
    if (n && n.parentNode && !n.classList?.contains('shloka')) {
      try { n.parentNode.removeChild(n); } catch { /* already removed */ }
    }
  });
  return root.innerHTML;
}

/**
 * Pass 2 only — wrap Takeaways/Medini/Country/Caution/Prediction callouts.
 * Runs AFTER both `enhanceLabelledShlokas` and `enhanceShlokas` so any
 * newly-wrapped `.shloka` blocks act as boundaries.
 */
export function enhanceAstroCallouts(html) {
  if (!html || typeof html !== 'string') return html || '';
  if (typeof DOMParser === 'undefined') return html;

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  } catch {
    return html;
  }
  const root = doc.getElementById('__root');
  if (!root) return html;

  // Same article-scoping fix as enhanceLabelledShlokas above.
  const articleWrap = root.querySelector('article.virat-blog') || root.querySelector('article');
  const scope = articleWrap || root;

  const currentChildren = Array.from(scope.children);
  const consumed2 = new Set();

  for (let i = 0; i < currentChildren.length; i++) {
    const el = currentChildren[i];
    if (!el || consumed2.has(el)) continue;
    const det = findSectionMatch(el);
    if (!det) continue;

    const startLevel = headingLevel(el) || 2;
    const body = [];
    let j = i + 1;
    while (j < currentChildren.length) {
      const nxt = currentChildren[j];
      if (!nxt) break;
      const lvl = headingLevel(nxt);
      if (lvl !== null && lvl <= startLevel) break;
      if (isBoundary(nxt) && !(headingLevel(nxt) && headingLevel(nxt) > startLevel)) {
        if (!headingLevel(nxt)) break;
      }
      body.push(nxt);
      j += 1;
    }
    if (body.length === 0) continue;

    const wrapper = doc.createElement('div');
    wrapper.className = det.cssClass;

    const header = doc.createElement('div');
    header.className = 'astro-callout-header';
    const iconSpan = doc.createElement('span');
    iconSpan.className = 'astro-callout-icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    iconSpan.textContent = det.icon;
    header.appendChild(iconSpan);
    const labelSpan = doc.createElement('span');
    labelSpan.className = 'astro-callout-label';
    labelSpan.textContent = det.label;
    header.appendChild(labelSpan);
    wrapper.appendChild(header);

    const bodyDiv = doc.createElement('div');
    bodyDiv.className = 'astro-callout-body';
    body.forEach((n) => { bodyDiv.appendChild(n); });
    wrapper.appendChild(bodyDiv);

    el.parentNode.replaceChild(wrapper, el);
    body.forEach((n) => consumed2.add(n));
  }
  return root.innerHTML;
}

export default enhanceAstroBlog;
